import { Suspense, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ErrorBoundary from '@/components/ErrorBoundary'
import { getRouteSurface } from '@/lib/hostRouting'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { initAnalytics, track } from '@/lib/analytics'
import PublicRoutes from '@/routes/PublicRoutes'
import { BRAND_LOGO } from '@/lib/brandAssets'

// Lazy so the studio surface (auth gates + the Supabase client they pull in) is
// never shipped to the public landing page. It only loads on the studio host.
const StudioRoutes = lazyWithReload(() => import('@/routes/StudioRoutes'))

const SIGN_IN_PATH_PATTERN = /^\/auth\/confirm\/?$/

function RouteLoadingFallback({ pathname }) {
  if (!SIGN_IN_PATH_PATTERN.test(pathname)) {
    return <div className="min-h-screen bg-background" />
  }

  return (
    <div className="min-h-screen bg-background text-foreground" role="status" aria-live="polite">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center px-4 sm:h-16 sm:px-5">
          <div className="inline-flex items-center gap-2 text-lg font-semibold tracking-tight">
            <picture>
              <source
                type="image/avif"
                srcSet={BRAND_LOGO.avifSrcSet}
                sizes="32px"
              />
              <img
                src={BRAND_LOGO.png}
                srcSet={BRAND_LOGO.pngSrcSet}
                sizes="32px"
                width="32"
                height="32"
                alt=""
                className="h-8 w-8 rounded object-cover"
              />
            </picture>
            <span>retira.app</span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-5 sm:py-14">
        <div className="max-w-xl">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Logging you in</h1>
          <p className="mt-4 text-sm leading-7 text-muted-foreground">
            We&apos;re securely confirming your sign-in. This can take a few seconds.
          </p>
          <div className="mt-8 h-2 w-full max-w-sm overflow-hidden bg-muted">
            <div className="h-full w-1/3 animate-pulse bg-primary" />
          </div>
        </div>
      </main>
    </div>
  )
}

// Reset scroll to the top whenever the route changes (logo, footer links, etc.).
function ScrollToTop() {
  const { hash, pathname } = useLocation()
  useEffect(() => {
    if (hash) {
      requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView())
      return
    }
    window.scrollTo(0, 0)
  }, [hash, pathname])
  return null
}

// De-identified page-view tracking for the data flywheel. Fires on every route
// change; the analytics client no-ops when unconfigured or opted out.
function AnalyticsTracker() {
  const { pathname } = useLocation()
  useEffect(() => {
    initAnalytics()
  }, [])
  useEffect(() => {
    track('view')
  }, [pathname])
  return null
}

function RuntimeRobotsMeta({ routeSurface }) {
  useEffect(() => {
    const metaSelector = 'meta[data-retira-runtime-robots="true"]'
    const existingMeta = document.head.querySelector(metaSelector)

    if (routeSurface !== 'studio') {
      existingMeta?.remove()
      return
    }

    const meta = existingMeta || document.createElement('meta')
    meta.setAttribute('name', 'robots')
    meta.setAttribute('content', 'noindex,nofollow')
    meta.setAttribute('data-retira-runtime-robots', 'true')

    if (!existingMeta) {
      document.head.appendChild(meta)
    }
  }, [routeSurface])

  return null
}

export default function App() {
  const { pathname } = useLocation()
  const routeSurface = getRouteSurface({ pathname })

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback pathname={pathname} />}>
        <ScrollToTop />
        <AnalyticsTracker />
        <RuntimeRobotsMeta routeSurface={routeSurface} />
        {routeSurface === 'studio' ? <StudioRoutes /> : <PublicRoutes />}
      </Suspense>
    </ErrorBoundary>
  )
}
